/**
 * Fornada Page - Gestão de Fornadas (Produção)
 */

import { useState, useEffect } from "react";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Search, Filter } from "lucide-react";
import { fornadasAdapter } from "@/data";
import { useToast } from "@/hooks/use-toast";
import { FornadaTable } from "@/components/fornada/FornadaTable";
import { FornadaFormDialog } from "@/components/fornada/FornadaFormDialog";
import { FornadaViewDialog } from "@/components/fornada/FornadaViewDialog";
import { FornadaDeleteDialog } from "@/components/fornada/FornadaDeleteDialog";
import type { Fornada, FornadaCompleta } from "@/types/fornada";

export default function Fornada() {
  const { toast } = useToast();
  const [fornadas, setFornadas] = useState<Fornada[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  
  // Dialogs
  const [showFormDialog, setShowFormDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [formMode, setFormMode] = useState<"create" | "edit">("create");
  const [selectedFornada, setSelectedFornada] = useState<FornadaCompleta | null>(null);
  const [fornadaToDelete, setFornadaToDelete] = useState<Fornada | null>(null);

  const loadFornadas = async () => {
    setLoading(true);
    try {
      const result = await fornadasAdapter.list({
        search,
        sort: "data_fornada",
        order: "desc",
      });

      setFornadas(result.items);
    } catch (error: any) {
      console.error("Error loading fornadas:", error);
      setFornadas([]);
      
      if (error && typeof error === 'object') {
        const errorMessage = error?.message || '';
        const errorCode = error?.code || '';
        
        if (
          !errorMessage.toLowerCase().includes('no rows') &&
          !errorMessage.toLowerCase().includes('empty') &&
          errorCode !== 'PGRST116'
        ) {
          toast({
            variant: "destructive",
            title: "Erro ao carregar fornadas",
            description: errorMessage || "Não foi possível carregar a lista de fornadas.",
          });
        }
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFornadas();
  }, [search]);

  const handleCreate = () => {
    setFormMode("create");
    setSelectedFornada(null);
    setShowFormDialog(true);
  };

  const handleEdit = async (fornada: Fornada) => {
    try {
      const itens = await fornadasAdapter.getItens(fornada.id_fornada);
      setFormMode("edit");
      setSelectedFornada({ fornada, itens });
      setShowFormDialog(true);
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Erro ao carregar fornada",
        description: error.message,
      });
    }
  };

  const handleView = async (fornada: Fornada) => {
    try {
      const itens = await fornadasAdapter.getItens(fornada.id_fornada);
      setSelectedFornada({ fornada, itens });
      setShowViewDialog(true);
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Erro ao carregar fornada",
        description: error.message,
      });
    }
  };

  const handleDelete = (fornada: Fornada) => {
    setFornadaToDelete(fornada);
    setShowDeleteDialog(true);
  };

  const handleDuplicate = async (id: string) => {
    try {
      const { id: novoId } = await fornadasAdapter.duplicar(id);
      toast({
        title: "Fornada duplicada com sucesso",
        description: `Nova fornada criada (ID: ${novoId.substring(0, 8)}...)`,
      });
      loadFornadas();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Erro ao duplicar fornada",
        description: error.message,
      });
    }
  };

  const handleFormSuccess = () => {
    setShowFormDialog(false);
    loadFornadas();
    toast({
      title: formMode === "create" ? "Fornada criada com sucesso" : "Fornada atualizada com sucesso",
    });
  };

  const handleDeleteSuccess = () => {
    setShowDeleteDialog(false);
    setFornadaToDelete(null);
    loadFornadas();
    toast({
      title: "Fornada excluída com sucesso",
      description: "A fornada foi removida do sistema.",
    });
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Fornadas</h1>
            <p className="text-muted-foreground">Gestão de produção de receitas</p>
          </div>
          <Button onClick={handleCreate} className="gap-2">
            <Plus className="w-4 h-4" />
            Nova Fornada
          </Button>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por responsável ou observações..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>

        {/* Table */}
        <FornadaTable
          fornadas={fornadas}
          loading={loading}
          onView={handleView}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onDuplicate={handleDuplicate}
        />

        {/* Dialogs */}
        <FornadaFormDialog
          open={showFormDialog}
          onOpenChange={setShowFormDialog}
          mode={formMode}
          fornada={selectedFornada}
          onSuccess={handleFormSuccess}
        />

        <FornadaViewDialog
          open={showViewDialog}
          onOpenChange={setShowViewDialog}
          fornada={selectedFornada}
          onEdit={() => {
            setShowViewDialog(false);
            if (selectedFornada) handleEdit(selectedFornada.fornada);
          }}
        />

        <FornadaDeleteDialog
          open={showDeleteDialog}
          onOpenChange={setShowDeleteDialog}
          fornada={fornadaToDelete}
          onConfirm={handleDeleteSuccess}
        />
      </div>
    </Layout>
  );
}
